/**
 * Cloudflare Workers Email Actions Client
 * Client-side API for email actions (delete, archive, unsubscribe)
 */

import type {
  SenderUnsubscribeOutcome,
  SenderUnsubscribeTier,
  UnsubscribeMethod,
  UnsubscribeValue,
} from '@/types/email-list.types';
import { getStoredSessionId } from './auth-client';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';

export interface BulkActionResult {
  status: 'success' | 'error';
  emailId: string;
  action?: string;
  error?: string;
  value?: UnsubscribeValue;
}

export interface BulkActionResponse {
  results: BulkActionResult[];
  summary: {
    successCount: number;
    errorCount: number;
  };
}

class EmailActionsError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public code?: string
  ) {
    super(message);
    this.name = 'EmailActionsError';
  }
}

/**
 * Client for email actions via Cloudflare Workers
 */
export class EmailActionsClient {
  private baseUrl: string;
  private accessToken: string;

  constructor(accessToken: string, baseUrl?: string) {
    this.baseUrl = baseUrl || CF_WORKER_URL;
    this.accessToken = accessToken;

    if (!this.baseUrl) {
      console.warn('VITE_CLOUDFLARE_WORKER_URL not configured');
    }
  }

  /**
   * Check if CF Worker is available
   */
  isCloudflareAvailable(): boolean {
    return Boolean(this.baseUrl);
  }

  private getAuthHeaders(): Record<string, string> {
    return this.accessToken
      ? { Authorization: `Bearer ${this.accessToken}` }
      : {};
  }

  /**
   * Make a request to the CF Worker
   */
  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;

    // Get session ID for logging
    const sessionId = getStoredSessionId();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.getAuthHeaders(),
    };
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }
    // Merge custom headers
    if (options.headers) {
      const customHeaders = options.headers as Record<string, string>;
      Object.assign(headers, customHeaders);
    }

    const response = await fetch(url, {
      ...options,
      credentials: 'include', // Include session cookie for logging
      headers,
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new EmailActionsError(
        data.error?.message || `Request failed: ${response.status}`,
        response.status,
        data.error?.code
      );
    }

    return data.data as T;
  }

  /**
   * Bulk action on emails (delete, archive, unsubscribe)
   */
  async bulkAction(
    action: 'delete' | 'archive' | 'unsubscribe',
    emailIds: string[]
  ): Promise<BulkActionResponse> {
    return this.request<BulkActionResponse>('/api/email-actions/bulk', {
      method: 'POST',
      body: JSON.stringify({
        action,
        emailIds,
        accessToken: this.accessToken,
      }),
    });
  }

  /**
   * Single email action (mark read, mark unread, delete, archive, spam)
   */
  async singleAction(
    emailId: string,
    action: 'mark_read' | 'mark_unread' | 'delete' | 'archive' | 'spam'
  ): Promise<void> {
    await this.request(`/api/email-actions/${emailId}/action`, {
      method: 'POST',
      body: JSON.stringify({
        action,
        accessToken: this.accessToken,
      }),
    });
  }

  /**
   * Get email content
   */
  async getEmailContent(emailId: string): Promise<{
    id: string;
    threadId: string;
    headers: {
      from: string | null;
      to: string | null;
      subject: string | null;
      date: string | null;
    };
    htmlBody: string | null;
    textBody: string | null;
    snippet: string;
  }> {
    return this.request(`/api/email-actions/${emailId}/content`);
  }

  /**
   * Handle unsubscribe request
   */
  async unsubscribe(params: {
    emailId: string;
    method: Extract<UnsubscribeMethod, 'none' | 'link' | 'post' | 'list-unsubscribe'>;
    unsubscribeLink?: string;
    formData?: Record<string, string>;
    listUnsubscribeHeader?: string;
    listUnsubscribePost?: string;
    userEmail: string;
  }): Promise<{
    success: boolean;
    message: string;
    emailId: string;
    senderPreferenceId?: string | null;
    suppressionApplied: boolean;
    senderUnsubscribeOutcome: SenderUnsubscribeOutcome;
    senderUnsubscribeTier: SenderUnsubscribeTier;
  }> {
    return this.request('/api/email-actions/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({
        ...params,
        accessToken: this.accessToken,
      }),
    });
  }

  /**
   * Undo a bulk action (delete, archive, unsubscribe)
   */
  async undoAction(
    action: 'delete' | 'archive' | 'unsubscribe',
    emailIds: string[],
    senderPreferenceIds?: string[]
  ): Promise<{
    results: Array<{ emailId: string; status: 'success' | 'error'; error?: string }>;
    summary: { successCount: number; errorCount: number; action: string };
  }> {
    return this.request('/api/email-actions/undo', {
      method: 'POST',
      body: JSON.stringify({
        action,
        emailIds,
        accessToken: this.accessToken,
        ...(senderPreferenceIds && senderPreferenceIds.length > 0
          ? { senderPreferenceIds }
          : {})
      }),
    });
  }
}

/**
 * Create email actions client from session
 */
export function createEmailActionsClient(accessToken: string): EmailActionsClient {
  return new EmailActionsClient(accessToken);
}

export { EmailActionsError };
